import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Feature Catalog Data
 * 92 features with professional, laymen-friendly descriptions
 * Includes 20 B2B dealer-focused features for wholesale diamond business
 */
import { FaCalculator, FaCalendarAlt, FaRing, FaBalanceScale, FaBrain, FaBell, FaCertificate, FaChartLine, FaSearch, FaSync, FaFileInvoiceDollar, FaBox, FaMobileAlt, FaCamera, FaUsers, FaShippingFast, FaHeart, FaGlobe, FaComments, FaShieldAlt, FaLink, FaCreditCard, FaRocket, FaEnvelope, FaStore, FaTags, FaBullhorn, FaGift, FaStar, FaHandshake, FaCube, FaLock, FaFileAlt, FaClipboardCheck, FaAward, FaChartBar, FaBullseye, FaGem, FaUserTie, FaHistory, FaMoneyBillWave, FaMedal, FaRedo, FaGavel, FaExchangeAlt, FaPercentage, FaUserShield, FaTrophy, FaChartPie, FaEye, FaBriefcase, FaPalette, FaBuilding, FaNetworkWired, FaClock, FaWarehouse, FaTruck, FaDollarSign, FaMapMarkedAlt, FaVideo, FaReceipt, FaLayerGroup, FaProjectDiagram } from 'react-icons/fa';
export const FEATURES = [
    // CORE FEATURES
    {
        id: 'price-calculator',
        title: 'Diamond Price Calculator',
        description: 'Real-time market price estimates based on the 4Cs (Carat, Color, Clarity, Cut) with instant wholesale vs retail comparison.',
        laymenDescription: 'Instantly see what a diamond is worth based on its quality. Like using Zillow to estimate home prices, but for diamonds.',
        realWorldComparison: 'Similar to: Zillow (real estate estimates), Kelley Blue Book (car values)',
        category: 'Core',
        tags: ['high-roi', 'quick-win', 'customer-experience'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Builds trust through transparency. Customers can verify they\'re getting fair prices.',
        icon: _jsx(FaCalculator, {}),
        color: '#3b82f6'
    },
    {
        id: 'advanced-search',
        title: 'Advanced Search & Filtering',
        description: 'Multi-criteria search engine with saved filters, search history, and smart diamond recommendations.',
        laymenDescription: 'Find exactly what you\'re looking for with powerful search. Save your favorite searches for next time.',
        realWorldComparison: 'Similar to: Amazon\'s advanced search, Airbnb filters',
        category: 'Core',
        tags: ['core', 'customer-experience', 'quick-win'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Customers find the right diamond faster. Less browsing, more buying.',
        icon: _jsx(FaSearch, {}),
        color: '#8b5cf6'
    },
    {
        id: 'inventory-sync',
        title: 'Automated Inventory Sync',
        description: 'Real-time inventory updates from supplier feeds (Rapnet, IDEX, Polygon) with automated pricing and availability.',
        laymenDescription: 'Your website automatically stays up-to-date with available diamonds. No manual entry needed.',
        realWorldComparison: 'Similar to: Stock market tickers auto-updating prices',
        category: 'Core',
        tags: ['core', 'operations', 'automation', 'real-time'],
        complexity: 'High',
        timeline: '4 weeks',
        value: 'Always show what\'s actually available. Prevents selling out-of-stock items.',
        icon: _jsx(FaSync, {}),
        color: '#10b981'
    },
    {
        id: 'automated-invoicing',
        title: 'Automated Invoicing & Billing',
        description: 'Professional invoice generation, payment tracking, reminders, and QuickBooks/Xero integration.',
        laymenDescription: 'Automatically create professional invoices and track who has paid. Connects to your accounting software.',
        realWorldComparison: 'Similar to: FreshBooks, QuickBooks Online',
        category: 'Core',
        tags: ['core', 'operations', 'automation', 'high-roi'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Saves hours of manual work. Looks professional. Never miss a payment.',
        icon: _jsx(FaFileInvoiceDollar, {}),
        color: '#f59e0b'
    },
    {
        id: 'order-management',
        title: 'Order Management System',
        description: 'Complete order lifecycle tracking from quote to delivery with status updates and customer notifications.',
        laymenDescription: 'See every order from start to finish in one place. Customers get automatic updates on their purchase.',
        realWorldComparison: 'Similar to: Amazon order tracking, FedEx shipment management',
        category: 'Core',
        tags: ['core', 'operations', 'customer-experience'],
        complexity: 'High',
        timeline: '4 weeks',
        value: 'Never lose track of an order. Customers feel informed and confident.',
        icon: _jsx(FaBox, {}),
        color: '#ef4444'
    },
    // INNOVATION FEATURES
    {
        id: 'ai-matchmaker',
        title: 'AI Diamond Matchmaker',
        description: 'Machine learning recommendations engine that learns customer preferences and suggests perfect matches.',
        laymenDescription: 'Like having a personal diamond expert who remembers what each customer likes and suggests perfect matches.',
        realWorldComparison: 'Similar to: Netflix recommendations, Spotify Discover Weekly',
        category: 'Innovation',
        tags: ['innovation', 'ai-powered', 'customer-experience', 'premium'],
        complexity: 'High',
        timeline: '6 weeks',
        value: 'Helps overwhelmed customers decide. Shows you understand their needs.',
        icon: _jsx(FaBrain, {}),
        color: '#ec4899'
    },
    {
        id: 'ar-try-on',
        title: 'AR Virtual Try-On',
        description: 'Augmented reality feature to visualize diamond size on hand using phone camera with accurate scale.',
        laymenDescription: 'Point your phone at your hand and see exactly how the diamond will look in real life.',
        realWorldComparison: 'Similar to: IKEA Place app, Warby Parker virtual try-on',
        category: 'Innovation',
        tags: ['innovation', 'mobile', 'customer-experience', 'premium'],
        complexity: 'Very High',
        timeline: '8 weeks',
        value: 'Customers see before they buy. Reduces size-related returns.',
        icon: _jsx(FaCamera, {}),
        color: '#06b6d4'
    },
    {
        id: 'blockchain-provenance',
        title: 'Blockchain Diamond Tracking',
        description: 'Immutable blockchain records of diamond journey from mine to customer with digital certificates.',
        laymenDescription: 'A permanent, tamper-proof record of where each diamond came from and who owned it. Like a digital birth certificate.',
        realWorldComparison: 'Similar to: Car title history (Carfax), property deed records',
        category: 'Innovation',
        tags: ['innovation', 'security', 'premium', 'enterprise'],
        complexity: 'Very High',
        timeline: '12 weeks',
        value: 'Ultimate proof of authenticity. Impossible to fake or alter.',
        icon: _jsx(FaCube, {}),
        color: '#8b7355'
    },
    {
        id: 'diamond-scanner',
        title: '3D Diamond Scanner Upload',
        description: 'Upload actual diamond scans for precise matching, authentication, and custom cut visualization.',
        laymenDescription: 'Send us a scan of your diamond and we\'ll find exact matches or verify it\'s authentic.',
        realWorldComparison: 'Similar to: Google Lens (image search), fingerprint authentication',
        category: 'Innovation',
        tags: ['innovation', 'technical', 'security'],
        complexity: 'Very High',
        timeline: '10 weeks',
        value: 'Verify authenticity. Match existing diamonds. Custom cut design.',
        icon: _jsx(FaGem, {}),
        color: '#6366f1'
    },
    {
        id: 'vr-showroom',
        title: 'Virtual Reality Showroom',
        description: 'Immersive VR experience where customers explore diamond collections in a virtual luxury showroom.',
        laymenDescription: 'Put on VR goggles and walk through a luxury diamond showroom from your home.',
        realWorldComparison: 'Similar to: Virtual real estate tours, Oculus home stores',
        category: 'Innovation',
        tags: ['innovation', 'premium', 'customer-experience'],
        complexity: 'Very High',
        timeline: '12 weeks',
        value: 'Wow factor. Perfect for remote luxury buyers.',
        icon: _jsx(FaCube, {}),
        color: '#a855f7'
    },
    {
        id: 'ai-chatbot',
        title: 'AI-Powered Chatbot',
        description: 'Natural language diamond search powered by GPT-4 with product recommendations and instant answers.',
        laymenDescription: 'Chat naturally like texting a friend: "I need a round diamond under $5000" and get instant matches.',
        realWorldComparison: 'Similar to: ChatGPT, customer service chat on websites',
        category: 'Innovation',
        tags: ['innovation', 'ai-powered', 'customer-experience', 'automation'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: '24/7 instant help. Captures leads even when you\'re sleeping.',
        icon: _jsx(FaComments, {}),
        color: '#14b8a6'
    },
    {
        id: 'predictive-alerts',
        title: 'Predictive Price Alerts',
        description: 'Machine learning predicts upcoming price drops and notifies dealers before market adjusts.',
        laymenDescription: 'Get a heads-up when diamond prices are about to drop, like a stock market tip before it happens.',
        realWorldComparison: 'Similar to: Stock price alerts, flight price predictions (Hopper)',
        category: 'Innovation',
        tags: ['innovation', 'ai-powered', 'high-roi', 'real-time'],
        complexity: 'High',
        timeline: '6 weeks',
        value: 'Buy low before competitors know. Maximize profit margins.',
        icon: _jsx(FaBullseye, {}),
        color: '#f97316'
    },
    {
        id: 'custom-cut-designer-public',
        title: 'Public Custom Cut Designer',
        description: 'Interactive tool for clients to design custom diamond cuts with instant pricing and feasibility feedback.',
        laymenDescription: 'Let customers design their dream diamond shape online and get an instant price quote.',
        realWorldComparison: 'Similar to: Nike shoe customizer, car configurators',
        category: 'Innovation',
        tags: ['innovation', 'customer-experience', 'premium'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Upsell custom work. Stand out from competitors.',
        icon: _jsx(FaGem, {}),
        color: '#ec4899'
    },
    // CUSTOMER EXPERIENCE
    {
        id: 'virtual-consultation',
        title: 'Virtual Consultation Booking',
        description: 'Schedule video calls with diamond experts directly from the website with calendar integration and automated reminders.',
        laymenDescription: 'Book a video call with a diamond expert like scheduling a doctor appointment. Get calendar invites and reminders.',
        realWorldComparison: 'Similar to: Calendly meeting scheduling, Zoom booking systems',
        category: 'Customer Experience',
        tags: ['customer-experience', 'quick-win', 'b2b', 'b2c'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Convert website browsers into engaged leads. Personal touch builds trust.',
        icon: _jsx(FaCalendarAlt, {}),
        color: '#06b6d4'
    },
    {
        id: 'custom-jewelry-builder',
        title: 'Custom Jewelry Builder',
        description: 'Interactive tool where customers select diamond, choose setting style, preview in 3D, and receive instant quote.',
        laymenDescription: 'Build your dream ring online: pick a diamond, choose the setting, see it in 3D, get a price. Like building a custom computer on Dell.com.',
        realWorldComparison: 'Similar to: Nike shoe customizer, Build-A-Bear online workshop',
        category: 'Customer Experience',
        tags: ['customer-experience', 'premium', 'high-roi'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Increases average order value. Customers love customization. Stands out from competition.',
        icon: _jsx(FaRing, {}),
        color: '#ec4899'
    },
    {
        id: 'comparison-tool',
        title: 'Diamond Comparison Tool',
        description: 'Side-by-side comparison with size overlay visualization and price-per-carat analysis.',
        laymenDescription: 'Compare diamonds side-by-side like comparing laptop specs on Best Buy. See differences clearly.',
        realWorldComparison: 'Similar to: Car comparison tools on Edmunds, phone comparisons on CNET',
        category: 'Customer Experience',
        tags: ['customer-experience', 'quick-win'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Helps confused customers make decisions. Keeps them on your site longer.',
        icon: _jsx(FaBalanceScale, {}),
        color: '#8b5cf6'
    },
    {
        id: 'wishlist-collections',
        title: 'Wishlist & Collections',
        description: 'Save favorite diamonds, create client collections, share via private link with collaboration features.',
        laymenDescription: 'Save favorites like Pinterest boards. Share your shortlist with family or business partner.',
        realWorldComparison: 'Similar to: Amazon Wish List, Pinterest boards',
        category: 'Customer Experience',
        tags: ['customer-experience', 'quick-win', 'b2b'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Brings customers back. Great for dealers sharing options with clients.',
        icon: _jsx(FaHeart, {}),
        color: '#f43f5e'
    },
    {
        id: 'inventory-alerts',
        title: 'Real-Time Inventory Alerts',
        description: 'Email and SMS notifications when diamonds matching customer criteria become available.',
        laymenDescription: 'Get texted when your perfect diamond arrives, like getting alerts when your favorite shoes are back in stock.',
        realWorldComparison: 'Similar to: Amazon price drop alerts, Zillow saved search notifications',
        category: 'Customer Experience',
        tags: ['customer-experience', 'automation', 'real-time'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Re-engages customers who didn\'t find what they wanted. Never lose a lead.',
        icon: _jsx(FaBell, {}),
        color: '#f59e0b'
    },
    {
        id: 'video-library',
        title: 'Diamond Video Library',
        description: '360-degree videos, magnified inspection views, and light performance demonstrations for each diamond.',
        laymenDescription: 'Watch high-quality videos showing every angle of the diamond. See how it sparkles in different lighting.',
        realWorldComparison: 'Similar to: Car walk-around videos on dealership sites, 360 product views on Apple.com',
        category: 'Customer Experience',
        tags: ['customer-experience', 'premium', 'quick-win'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Builds confidence for remote buyers. Reduces "need to see in person" objections.',
        icon: _jsx(FaCamera, {}),
        color: '#3b82f6'
    },
    {
        id: 'client-portal',
        title: 'Client Portal',
        description: 'Dedicated login area where end clients (not dealers) can track orders, view history, and manage account.',
        laymenDescription: 'Give your customers their own account to track orders and see purchase history, like Amazon or FedEx tracking.',
        realWorldComparison: 'Similar to: UPS My Choice, Amazon account dashboard',
        category: 'Customer Experience',
        tags: ['customer-experience', 'b2c', 'operations'],
        complexity: 'High',
        timeline: '4 weeks',
        value: 'White-label service quality. Reduces "where\'s my order?" calls.',
        icon: _jsx(FaUsers, {}),
        color: '#10b981'
    },
    {
        id: 'gift-registry',
        title: 'Gift Registry',
        description: 'Engagement ring wish lists that couples can share with family and friends for crowdfunding or gifting.',
        laymenDescription: 'Like a wedding registry but for the engagement ring. Couples share their wishlist, family can contribute.',
        realWorldComparison: 'Similar to: The Knot registry, Target wedding registry',
        category: 'Customer Experience',
        tags: ['customer-experience', 'b2c', 'innovation'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Tap into a new market segment. Creates emotional connection.',
        icon: _jsx(FaGift, {}),
        color: '#f472b6'
    },
    {
        id: 'live-chat',
        title: 'Live Chat Support',
        description: 'Real-time chat with AI handling common questions and seamless handoff to human experts.',
        laymenDescription: 'Instant chat help on the website. AI answers simple questions, humans take over for complex ones.',
        realWorldComparison: 'Similar to: Chat support on Apple.com, Intercom on SaaS sites',
        category: 'Customer Experience',
        tags: ['customer-experience', 'ai-powered', 'automation'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Capture leads 24/7. Instant support increases conversion.',
        icon: _jsx(FaComments, {}),
        color: '#14b8a6'
    },
    {
        id: 'mobile-app',
        title: 'Mobile Companion App',
        description: 'Native iOS and Android app for on-the-go browsing with push notifications and AR features.',
        laymenDescription: 'A full mobile app like Instagram or Uber, but for diamond shopping. Browse anywhere, get notifications.',
        realWorldComparison: 'Similar to: Etsy mobile app, eBay mobile app',
        category: 'Customer Experience',
        tags: ['customer-experience', 'mobile', 'premium'],
        complexity: 'Very High',
        timeline: '12 weeks',
        value: 'Always accessible. Push notifications re-engage customers. Premium brand perception.',
        icon: _jsx(FaMobileAlt, {}),
        color: '#6366f1'
    },
    // OPERATIONS
    {
        id: 'crm-integration',
        title: 'CRM Integration',
        description: 'Two-way sync with Salesforce, HubSpot, or Pipedrive for seamless customer relationship management.',
        laymenDescription: 'Your customer data automatically syncs with your CRM software. No duplicate data entry.',
        realWorldComparison: 'Similar to: Shopify syncing with Salesforce, email syncing with phone contacts',
        category: 'Operations',
        tags: ['operations', 'integration', 'automation', 'enterprise'],
        complexity: 'High',
        timeline: '4 weeks',
        value: 'One source of truth. Sales team has complete customer history.',
        icon: _jsx(FaHandshake, {}),
        color: '#0ea5e9'
    },
    {
        id: 'email-automation',
        title: 'Email Marketing Automation',
        description: 'Automated drip campaigns, abandoned cart recovery, new inventory announcements, and personalized recommendations.',
        laymenDescription: 'Automatic emails that go out at the right time: cart reminders, new arrivals, birthday wishes. Like Mailchimp built-in.',
        realWorldComparison: 'Similar to: Mailchimp automations, Amazon "items you might like" emails',
        category: 'Operations',
        tags: ['operations', 'marketing', 'automation', 'high-roi'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Recover lost sales. Re-engage dormant customers. Professional communication.',
        icon: _jsx(FaEnvelope, {}),
        color: '#8b5cf6'
    },
    {
        id: 'shipping-integration',
        title: 'Shipping Integration',
        description: 'Direct integration with FedEx, UPS, and DHL for automated label creation, tracking, and insurance.',
        laymenDescription: 'Print shipping labels with one click. Customers get tracking automatically. Like how Amazon ships packages.',
        realWorldComparison: 'Similar to: ShipStation, Shopify shipping',
        category: 'Operations',
        tags: ['operations', 'integration', 'automation'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Save hours per week. Professional tracking experience. Automatic insurance.',
        icon: _jsx(FaShippingFast, {}),
        color: '#f97316'
    },
    {
        id: 'accounting-sync',
        title: 'Accounting Software Sync',
        description: 'Automatic synchronization with QuickBooks, Xero, or NetSuite for financial accuracy.',
        laymenDescription: 'Sales, invoices, and payments automatically appear in QuickBooks. No manual bookkeeping.',
        realWorldComparison: 'Similar to: Square syncing with QuickBooks, PayPal integration',
        category: 'Operations',
        tags: ['operations', 'integration', 'automation', 'enterprise'],
        complexity: 'High',
        timeline: '4 weeks',
        value: 'Tax time made easy. Real-time financial picture. Accountant will love you.',
        icon: _jsx(FaCreditCard, {}),
        color: '#10b981'
    },
    {
        id: 'bulk-operations',
        title: 'Bulk Operations Dashboard',
        description: 'Mass update prices, status, categories, and other fields across multiple diamonds simultaneously.',
        laymenDescription: 'Update hundreds of diamonds at once instead of one-by-one. Like editing multiple files at once on your computer.',
        realWorldComparison: 'Similar to: Excel bulk operations, Gmail selecting all emails',
        category: 'Operations',
        tags: ['operations', 'quick-win', 'technical'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Save massive time. Make market adjustments instantly.',
        icon: _jsx(FaClipboardCheck, {}),
        color: '#3b82f6'
    },
    {
        id: 'supplier-portal',
        title: 'Supplier Portal',
        description: 'External portal where suppliers can directly upload inventory, update prices, and manage their listings.',
        laymenDescription: 'Give suppliers their own login to add diamonds themselves. You review and approve. No more manual data entry.',
        realWorldComparison: 'Similar to: Amazon seller portal, Etsy shop management',
        category: 'Operations',
        tags: ['operations', 'b2b', 'automation'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Scales your business. Reduces data entry. More suppliers, more inventory.',
        icon: _jsx(FaStore, {}),
        color: '#ec4899'
    },
    {
        id: 'auto-repricing',
        title: 'Auto-Repricing Engine',
        description: 'AI-powered dynamic pricing that adjusts based on market data, competition, and demand to maximize margins.',
        laymenDescription: 'Prices automatically adjust to stay competitive, like how Uber surge pricing works or how hotels change rates.',
        realWorldComparison: 'Similar to: Amazon\'s dynamic pricing, airline ticket pricing',
        category: 'Operations',
        tags: ['operations', 'ai-powered', 'automation', 'high-roi'],
        complexity: 'Very High',
        timeline: '8 weeks',
        value: 'Never leave money on the table. Always competitive. Maximize profit.',
        icon: _jsx(FaTags, {}),
        color: '#ef4444'
    },
    {
        id: 'quality-checklist',
        title: 'Quality Control Checklist',
        description: 'Digital inspection forms with photo uploads, approval workflows, and quality assurance tracking.',
        laymenDescription: 'Digital checklists for inspecting diamonds with photos. Track what\'s been checked, who checked it, when.',
        realWorldComparison: 'Similar to: Restaurant health inspection checklists, car inspection reports',
        category: 'Operations',
        tags: ['operations', 'quick-win', 'non-technical'],
        complexity: 'Low',
        timeline: '2 weeks',
        value: 'Consistency in quality. Compliance documentation. Reduce errors.',
        icon: _jsx(FaClipboardCheck, {}),
        color: '#14b8a6'
    },
    {
        id: 'document-management',
        title: 'Document Management System',
        description: 'Centralized storage for certificates, invoices, contracts with version control and search.',
        laymenDescription: 'All your documents in one organized place with search. Like Google Drive but designed for diamond documents.',
        realWorldComparison: 'Similar to: Google Drive, Dropbox for Business',
        category: 'Operations',
        tags: ['operations', 'compliance', 'quick-win'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Never lose paperwork. Find documents instantly. Audit trail for compliance.',
        icon: _jsx(FaFileAlt, {}),
        color: '#6366f1'
    },
    {
        id: 'staff-performance',
        title: 'Staff Performance Dashboard',
        description: 'Track sales metrics, response times, customer satisfaction scores, and individual performance.',
        laymenDescription: 'See which team members are closing deals, responding quickly, and getting good reviews. Like a sales leaderboard.',
        realWorldComparison: 'Similar to: Salesforce dashboards, retail store performance metrics',
        category: 'Operations',
        tags: ['operations', 'analytics', 'enterprise'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Accountability. Identify top performers. Coach improvement.',
        icon: _jsx(FaChartBar, {}),
        color: '#f59e0b'
    },
    // ANALYTICS
    {
        id: 'market-trends',
        title: 'Market Trends Dashboard',
        description: 'Visual analytics showing price trends, demand patterns, and investment insights for different diamond types.',
        laymenDescription: 'See which diamonds are trending up or down in price. Like stock market charts but for diamonds.',
        realWorldComparison: 'Similar to: Stock market dashboards, Zillow home value trends',
        category: 'Analytics',
        tags: ['analytics', 'high-roi', 'premium'],
        complexity: 'High',
        timeline: '4 weeks',
        value: 'Buy smart. Thought leadership content. Educate customers.',
        icon: _jsx(FaChartLine, {}),
        color: '#3b82f6'
    },
    {
        id: 'customer-behavior',
        title: 'Customer Behavior Analytics',
        description: 'Heat maps, click tracking, conversion funnels, and user journey analysis to optimize website experience.',
        laymenDescription: 'See where customers click, what they ignore, where they drop off. Like watching security camera footage of your website.',
        realWorldComparison: 'Similar to: Google Analytics, Hotjar heat maps',
        category: 'Analytics',
        tags: ['analytics', 'marketing', 'technical'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Fix what\'s broken. Double down on what works. Data-driven decisions.',
        icon: _jsx(FaChartBar, {}),
        color: '#8b5cf6'
    },
    {
        id: 'inventory-performance',
        title: 'Inventory Performance Reports',
        description: 'Track time-to-sell, margin analysis, dead stock alerts, and inventory turnover metrics.',
        laymenDescription: 'Know which diamonds sell fast vs. collect dust. See your profit margins. Like having a business advisor.',
        realWorldComparison: 'Similar to: Retail inventory reports, car dealership aging reports',
        category: 'Analytics',
        tags: ['analytics', 'operations', 'high-roi'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Stock what sells. Identify slow movers. Optimize cash flow.',
        icon: _jsx(FaChartLine, {}),
        color: '#10b981'
    },
    {
        id: 'competitor-monitoring',
        title: 'Competitor Price Monitoring',
        description: 'Automated tracking of competitor pricing with alerts when you\'re being undercut on similar diamonds.',
        laymenDescription: 'Know when competitors drop prices below yours. Like having someone constantly shopping your competition.',
        realWorldComparison: 'Similar to: Price tracking on CamelCamelCamel, gas price comparison apps',
        category: 'Analytics',
        tags: ['analytics', 'automation', 'high-roi'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Stay competitive. React fast to market changes. Never lose on price.',
        icon: _jsx(FaBullseye, {}),
        color: '#ef4444'
    },
    {
        id: 'demand-forecasting',
        title: 'Predictive Demand Forecasting',
        description: 'AI predicts which diamond types will sell based on trends, seasonality, and historical data.',
        laymenDescription: 'AI predicts what will sell next month so you stock the right diamonds. Like weather forecasting for your business.',
        realWorldComparison: 'Similar to: Retail demand planning, Amazon inventory predictions',
        category: 'Analytics',
        tags: ['analytics', 'ai-powered', 'enterprise'],
        complexity: 'Very High',
        timeline: '8 weeks',
        value: 'Reduce holding costs. Optimize purchasing. Less dead inventory.',
        icon: _jsx(FaBrain, {}),
        color: '#ec4899'
    },
    {
        id: 'customer-ltv',
        title: 'Client Lifetime Value Tracker',
        description: 'Track total client purchases, predict future value, and segment into tiers for targeted treatment.',
        laymenDescription: 'See which customers spend the most over time. Treat VIPs special. Like airline status tiers.',
        realWorldComparison: 'Similar to: Airline frequent flyer programs, credit card rewards tiers',
        category: 'Analytics',
        tags: ['analytics', 'b2b', 'marketing'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Focus on high-value clients. Retention strategies. VIP treatment.',
        icon: _jsx(FaStar, {}),
        color: '#f59e0b'
    },
    {
        id: 'sales-dashboard',
        title: 'Real-Time Sales Dashboard',
        description: 'Live dashboard showing today\'s sales, weekly trends, monthly charts, and goal tracking.',
        laymenDescription: 'See sales happening in real-time like a scoreboard. Daily, weekly, monthly views. Know where you stand.',
        realWorldComparison: 'Similar to: Shopify dashboard, Square daily sales report',
        category: 'Analytics',
        tags: ['analytics', 'real-time', 'quick-win'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Instant visibility. Motivate team. Track goals.',
        icon: _jsx(FaRocket, {}),
        color: '#06b6d4'
    },
    // INTEGRATION
    {
        id: 'gia-integration',
        title: 'GIA/AGS API Integration',
        description: 'Automatic verification of diamond certificates through official GIA and AGS APIs.',
        laymenDescription: 'Instantly verify certificates are real by connecting to GIA\'s database. Like checking if a bill is counterfeit.',
        realWorldComparison: 'Similar to: VIN check for cars, bank account verification',
        category: 'Integration',
        tags: ['integration', 'security', 'quick-win'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Trust and authenticity. Fraud prevention. Instant verification.',
        icon: _jsx(FaCertificate, {}),
        color: '#10b981'
    },
    {
        id: 'social-auto-post',
        title: 'Social Media Auto-Posting',
        description: 'Automatically post new inventory to Instagram, Facebook, and other platforms with templates.',
        laymenDescription: 'New diamonds automatically post to Instagram and Facebook. Like having a social media manager.',
        realWorldComparison: 'Similar to: Buffer, Hootsuite auto-scheduling',
        category: 'Integration',
        tags: ['integration', 'marketing', 'automation'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Consistent social presence. No manual posting. Increased visibility.',
        icon: _jsx(FaBullhorn, {}),
        color: '#ec4899'
    },
    {
        id: 'zapier-integration',
        title: 'Zapier/Make Integration',
        description: 'Connect to 5000+ apps through Zapier or Make for unlimited automation possibilities.',
        laymenDescription: 'Connect to any app you use: Slack, Google Sheets, Airtable, etc. Like universal power adapters but for software.',
        realWorldComparison: 'Similar to: IFTTT automation, Zapier workflows',
        category: 'Integration',
        tags: ['integration', 'automation', 'enterprise'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Unlimited flexibility. Connect your tools. Custom workflows.',
        icon: _jsx(FaLink, {}),
        color: '#f97316'
    },
    {
        id: 'payment-gateway',
        title: 'Payment Gateway Integration',
        description: 'Accept credit cards, PayPal, wire transfers, and cryptocurrency through integrated payment processors.',
        laymenDescription: 'Accept payments online like any e-commerce site. Credit cards, PayPal, even Bitcoin.',
        realWorldComparison: 'Similar to: Shopify checkout, Amazon payments',
        category: 'Integration',
        tags: ['integration', 'core', 'quick-win'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Close deals online. Multiple payment options. Secure processing.',
        icon: _jsx(FaCreditCard, {}),
        color: '#14b8a6'
    },
    {
        id: 'google-shopping',
        title: 'Google Shopping Feed',
        description: 'Automatically generate and sync product feed for Google Shopping ads and organic listings.',
        laymenDescription: 'Your diamonds appear in Google Shopping results automatically. Like having a storefront in Google.',
        realWorldComparison: 'Similar to: Product listings on Amazon, eBay search results',
        category: 'Integration',
        tags: ['integration', 'marketing', 'high-roi'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'New traffic source. High-intent customers. Competitive visibility.',
        icon: _jsx(FaSearch, {}),
        color: '#3b82f6'
    },
    {
        id: 'dynamics-integration',
        title: 'Microsoft Dynamics 365',
        description: 'Full ERP integration for enterprise clients with complex operations and accounting needs.',
        laymenDescription: 'Connect to Microsoft\'s enterprise business software. For serious operations with complex needs.',
        realWorldComparison: 'Similar to: SAP integration, Oracle ERP connection',
        category: 'Integration',
        tags: ['integration', 'enterprise', 'operations'],
        complexity: 'Very High',
        timeline: '10 weeks',
        value: 'Enterprise-grade operations. Complete business system integration.',
        icon: _jsx(FaSync, {}),
        color: '#0078d4'
    },
    // SECURITY
    {
        id: 'two-factor-auth',
        title: 'Two-Factor Authentication',
        description: 'Enhanced security with SMS, email, or authenticator app verification for account access.',
        laymenDescription: 'Extra security layer like online banking. Enter code from phone to login. Prevents hackers.',
        realWorldComparison: 'Similar to: Bank account 2FA, Gmail security verification',
        category: 'Security',
        tags: ['security', 'quick-win', 'core'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Account protection. Industry best practice. Customer confidence.',
        icon: _jsx(FaLock, {}),
        color: '#ef4444'
    },
    {
        id: 'audit-trail',
        title: 'Audit Trail System',
        description: 'Complete logging of all system actions with GDPR compliance and exportable reports.',
        laymenDescription: 'Record of who did what, when. Like security camera logs but for data. Required for compliance.',
        realWorldComparison: 'Similar to: Bank transaction history, medical records access logs',
        category: 'Security',
        tags: ['security', 'compliance', 'enterprise'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Compliance requirement. Accountability. Dispute resolution.',
        icon: _jsx(FaFileAlt, {}),
        color: '#6366f1'
    },
    {
        id: 'ip-whitelisting',
        title: 'IP Whitelisting',
        description: 'Restrict system access to specific IP addresses or geographic regions for enhanced security.',
        laymenDescription: 'Only allow logins from certain locations or offices. Like only opening your door to people on a list.',
        realWorldComparison: 'Similar to: Office building key card access, VPN restrictions',
        category: 'Security',
        tags: ['security', 'enterprise', 'technical'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Enhanced security for high-value operations. Geo-restrictions.',
        icon: _jsx(FaShieldAlt, {}),
        color: '#8b5cf6'
    },
    {
        id: 'insurance-integration',
        title: 'Diamond Insurance Integration',
        description: 'Instant insurance quotes and policy management through partner insurance providers.',
        laymenDescription: 'Offer insurance quotes at checkout like extended warranties at Best Buy. Value-added service.',
        realWorldComparison: 'Similar to: Car rental insurance, phone protection plans',
        category: 'Security',
        tags: ['integration', 'security', 'premium'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Additional revenue stream. Customer peace of mind. Premium service.',
        icon: _jsx(FaShieldAlt, {}),
        color: '#10b981'
    },
    {
        id: 'gdpr-compliance',
        title: 'GDPR Compliance Tools',
        description: 'Data export, right to deletion, consent management, and privacy compliance automation.',
        laymenDescription: 'Tools to comply with privacy laws. Customers can download or delete their data. Required in Europe.',
        realWorldComparison: 'Similar to: Facebook "Download Your Information", Google account data export',
        category: 'Security',
        tags: ['security', 'compliance', 'non-technical'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Legal compliance. Avoid fines. Trust and transparency.',
        icon: _jsx(FaFileAlt, {}),
        color: '#3b82f6'
    },
    // MARKETING
    {
        id: 'referral-program',
        title: 'Referral Program',
        description: 'Automated referral tracking, rewards distribution, and affiliate dashboard with commission management.',
        laymenDescription: 'Customers refer friends, get rewards automatically. Like Dropbox giving free storage for referrals.',
        realWorldComparison: 'Similar to: Uber referral credits, Airbnb invite bonuses',
        category: 'Marketing',
        tags: ['marketing', 'automation', 'high-roi'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Viral growth. Customers become salespeople. Low cost acquisition.',
        icon: _jsx(FaHandshake, {}),
        color: '#10b981'
    },
    {
        id: 'influencer-portal',
        title: 'Influencer Partnership Portal',
        description: 'Influencer application system, performance tracking, and automated commission payments.',
        laymenDescription: 'Manage Instagram influencers promoting your diamonds. Track sales, pay commissions automatically.',
        realWorldComparison: 'Similar to: Amazon Associates, affiliate programs on major brands',
        category: 'Marketing',
        tags: ['marketing', 'b2c', 'innovation'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Brand awareness. Reach new audiences. Performance-based marketing.',
        icon: _jsx(FaStar, {}),
        color: '#ec4899'
    },
    {
        id: 'email-capture',
        title: 'Email Capture & Popups',
        description: 'Exit-intent, scroll-based, and timed popups with A/B testing to build email list.',
        laymenDescription: 'Smart popups that appear at the right time to capture emails. Like popups on news sites but not annoying.',
        realWorldComparison: 'Similar to: Newsletter popups on blogs, exit offers on retail sites',
        category: 'Marketing',
        tags: ['marketing', 'quick-win', 'high-roi'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Build email list. Capture leaving visitors. High ROI marketing.',
        icon: _jsx(FaEnvelope, {}),
        color: '#f59e0b'
    },
    {
        id: 'content-cms',
        title: 'Content Marketing CMS',
        description: 'Built-in blog and education center for SEO-optimized articles and diamond education content.',
        laymenDescription: 'Easy blog system to write articles. Helps Google find you. Positions you as expert.',
        realWorldComparison: 'Similar to: WordPress blog, Medium publication',
        category: 'Marketing',
        tags: ['marketing', 'non-technical', 'quick-win'],
        complexity: 'Low',
        timeline: '2 weeks',
        value: 'Organic traffic. Thought leadership. SEO benefits.',
        icon: _jsx(FaFileAlt, {}),
        color: '#8b5cf6'
    },
    {
        id: 'retargeting-pixels',
        title: 'Retargeting Pixel Integration',
        description: 'Facebook, Google, and TikTok pixels for ad retargeting campaigns to bring visitors back.',
        laymenDescription: 'Show ads to people who visited your site. Like how Amazon ads follow you around the internet.',
        realWorldComparison: 'Similar to: Facebook retargeting, Google remarketing',
        category: 'Marketing',
        tags: ['marketing', 'integration', 'high-roi'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Convert abandoners. High-intent audience. Lower ad costs.',
        icon: _jsx(FaBullseye, {}),
        color: '#3b82f6'
    },
    {
        id: 'diamond-quiz',
        title: 'Interactive Diamond Quiz',
        description: 'Engaging "Find your perfect diamond" quiz that collects leads while providing personalized recommendations.',
        laymenDescription: 'Fun quiz that helps customers find their style while you capture their email. Like BuzzFeed quizzes.',
        realWorldComparison: 'Similar to: Warby Parker style quiz, skincare recommendation quizzes',
        category: 'Marketing',
        tags: ['marketing', 'customer-experience', 'quick-win'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Engagement. Lead capture. Qualify prospects.',
        icon: _jsx(FaBullseye, {}),
        color: '#06b6d4'
    },
    {
        id: 'seasonal-promos',
        title: 'Seasonal Promotions Engine',
        description: 'Automated holiday sales, countdown timers, promo codes, and scheduled campaigns.',
        laymenDescription: 'Set up sales for holidays automatically. Countdown timers, discount codes, the works.',
        realWorldComparison: 'Similar to: Amazon Prime Day, Black Friday sale automation',
        category: 'Marketing',
        tags: ['marketing', 'automation', 'quick-win'],
        complexity: 'Low',
        timeline: '2 weeks',
        value: 'Seasonal revenue boost. Urgency creation. Automated campaigns.',
        icon: _jsx(FaGift, {}),
        color: '#dc2626'
    },
    // ADDITIONAL CATEGORIES
    {
        id: 'pwa',
        title: 'Progressive Web App',
        description: 'Installable web app with offline access, push notifications, and app-like performance.',
        laymenDescription: 'Website that works like an app. Install on phone, works without internet, get notifications.',
        realWorldComparison: 'Similar to: Twitter Lite, Starbucks PWA',
        category: 'Mobile',
        tags: ['mobile', 'customer-experience', 'technical'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'App benefits without app store. Works offline. Push notifications.',
        icon: _jsx(FaMobileAlt, {}),
        color: '#14b8a6'
    },
    {
        id: 'multi-language',
        title: 'Multi-Language Support',
        description: 'Support for Chinese, Arabic, Spanish, French, Hebrew with right-to-left language support.',
        laymenDescription: 'Website in multiple languages for international customers. Like how Apple.com has country versions.',
        realWorldComparison: 'Similar to: Netflix language options, Google Translate for websites',
        category: 'International',
        tags: ['internationalization', 'customer-experience', 'enterprise'],
        complexity: 'High',
        timeline: '4 weeks',
        value: 'Global expansion. Serve international clients. Market access.',
        icon: _jsx(FaGlobe, {}),
        color: '#3b82f6'
    },
    {
        id: 'multi-currency',
        title: 'Multi-Currency Pricing',
        description: 'Display and accept payments in multiple currencies with automatic conversion.',
        laymenDescription: 'Show prices in customer\'s currency. Accept payment in their money. Like shopping internationally on Amazon.',
        realWorldComparison: 'Similar to: Amazon international, booking.com currency options',
        category: 'International',
        tags: ['internationalization', 'operations', 'enterprise'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'International sales. Remove purchase friction. Professional global presence.',
        icon: _jsx(FaGlobe, {}),
        color: '#10b981'
    },
    {
        id: 'white-label',
        title: 'White Label Platform',
        description: 'Completely rebrand the platform for enterprise clients with custom domain and branding.',
        laymenDescription: 'Let other businesses use your platform with their brand. Like how Shopify powers other stores.',
        realWorldComparison: 'Similar to: Shopify powering other stores, white-label SaaS',
        category: 'Premium',
        tags: ['enterprise', 'premium', 'b2b'],
        complexity: 'Very High',
        timeline: '10 weeks',
        value: 'New revenue stream. B2B opportunity. Scalable business model.',
        icon: _jsx(FaStore, {}),
        color: '#8b5cf6'
    },
    {
        id: 'api-access',
        title: 'API Access for Partners',
        description: 'RESTful API allowing partners to integrate inventory data into their own systems.',
        laymenDescription: 'Let partners connect their software to yours. Like how weather apps get data from weather services.',
        realWorldComparison: 'Similar to: Google Maps API, Stripe API for developers',
        category: 'Technical',
        tags: ['enterprise', 'technical', 'b2b'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Ecosystem growth. Partner integration. Platform play.',
        icon: _jsx(FaLink, {}),
        color: '#6366f1'
    },
    {
        id: 'auction-platform',
        title: 'Auction Platform',
        description: 'Timed auctions for rare and estate diamonds with bidding, notifications, and automatic winner selection.',
        laymenDescription: 'eBay-style auctions for special diamonds. Bidding wars drive up prices. Great for rare pieces.',
        realWorldComparison: 'Similar to: eBay auctions, Christie\'s online auctions',
        category: 'Premium',
        tags: ['premium', 'innovation', 'high-roi'],
        complexity: 'Very High',
        timeline: '10 weeks',
        value: 'New sales channel. Excitement and urgency. Market value discovery.',
        icon: _jsx(FaBalanceScale, {}),
        color: '#f59e0b'
    },
    {
        id: 'social-proof',
        title: 'Social Proof Widgets',
        description: 'Live notifications of recent purchases, review counts, and trust badges throughout the site.',
        laymenDescription: 'Show "John just bought a diamond" popups. Review counts. Trust badges. Like booking.com does.',
        realWorldComparison: 'Similar to: Booking.com "X people viewing", Amazon review counts',
        category: 'Marketing',
        tags: ['marketing', 'customer-experience', 'quick-win'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Conversion boost. Trust building. FOMO effect.',
        icon: _jsx(FaAward, {}),
        color: '#f97316'
    },
    {
        id: 'review-system',
        title: 'Review & Rating System',
        description: 'Verified buyer reviews with photo uploads, moderation tools, and SEO-rich snippets.',
        laymenDescription: 'Customers leave reviews like Amazon. Photos included. You can moderate. Helps Google ranking.',
        realWorldComparison: 'Similar to: Amazon reviews, Yelp ratings',
        category: 'Customer Experience',
        tags: ['customer-experience', 'marketing', 'quick-win'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Trust and credibility. User-generated content. SEO benefits.',
        icon: _jsx(FaStar, {}),
        color: '#f59e0b'
    },
    {
        id: 'education-center',
        title: 'Diamond Education Center',
        description: 'Interactive 4Cs guide, educational videos, quizzes, and comprehensive diamond knowledge base.',
        laymenDescription: 'Teach customers about diamonds with videos and interactive guides. Like Khan Academy but for diamonds.',
        realWorldComparison: 'Similar to: Khan Academy lessons, interactive learning platforms',
        category: 'Education',
        tags: ['customer-experience', 'marketing', 'non-technical'],
        complexity: 'Low',
        timeline: '2 weeks',
        value: 'Build trust. Reduce questions. Establish expertise.',
        icon: _jsx(FaBalanceScale, {}),
        color: '#3b82f6'
    },
    {
        id: 'cert-verification',
        title: 'Certification Verification Tool',
        description: 'Public tool where anyone can enter a GIA certificate number and see the full report.',
        laymenDescription: 'Let anyone verify a certificate is real by entering the number. Like checking if money is counterfeit.',
        realWorldComparison: 'Similar to: VIN lookup for cars, trademark search tools',
        category: 'Security',
        tags: ['security', 'quick-win', 'customer-experience'],
        complexity: 'Low',
        timeline: '1 week',
        value: 'Trust building. Anti-fraud. Public service.',
        icon: _jsx(FaCertificate, {}),
        color: '#10b981'
    },
    {
        id: 'smart-inventory',
        title: 'Smart Inventory Recommendations',
        description: 'AI analyzes trends and suggests which diamonds to purchase for optimal inventory mix.',
        laymenDescription: 'AI tells you what to buy based on what\'s selling. Like having a data analyst predicting trends.',
        realWorldComparison: 'Similar to: Retail buying recommendations, Netflix content decisions',
        category: 'Automation',
        tags: ['ai-powered', 'operations', 'high-roi'],
        complexity: 'Very High',
        timeline: '8 weeks',
        value: 'Optimize purchasing. Reduce dead stock. Data-driven buying.',
        icon: _jsx(FaBrain, {}),
        color: '#ec4899'
    },
    {
        id: 'automated-followup',
        title: 'Automated Follow-Up System',
        description: 'AI determines optimal timing and personalization for follow-up emails to maximize conversion.',
        laymenDescription: 'AI figures out the best time to email each customer with personalized messages. Like a smart assistant.',
        realWorldComparison: 'Similar to: Email marketing AI timing, smart send features',
        category: 'Automation',
        tags: ['automation', 'ai-powered', 'marketing'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Higher conversion. Perfect timing. Personalization at scale.',
        icon: _jsx(FaEnvelope, {}),
        color: '#14b8a6'
    },
    {
        id: 'image-search',
        title: 'Image Recognition Diamond Search',
        description: 'Upload a photo of any diamond and find visually similar matches in inventory using AI.',
        laymenDescription: 'Take a picture of a diamond, find similar ones. Like Google Lens but for diamonds.',
        realWorldComparison: 'Similar to: Google Lens, Pinterest visual search',
        category: 'Innovation',
        tags: ['ai-powered', 'innovation', 'customer-experience'],
        complexity: 'Very High',
        timeline: '10 weeks',
        value: 'Unique search capability. Match existing pieces. Competitive advantage.',
        icon: _jsx(FaCamera, {}),
        color: '#6366f1'
    },
    // B2B & DEALER TOOLS
    {
        id: 'credit-limit-management',
        title: 'Credit Limit Management',
        description: 'Set and manage credit limits for dealer accounts with automatic enforcement, credit usage tracking, and payment terms.',
        laymenDescription: 'Give dealers credit lines like a business credit card. Set limits, track usage, enforce payment terms automatically.',
        realWorldComparison: 'Similar to: Business credit cards, wholesale trade credit, vendor payment terms',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'high-roi', 'operations'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Enable larger purchases. Build dealer relationships. Secure payment management.',
        icon: _jsx(FaUserTie, {}),
        color: '#0ea5e9'
    },
    {
        id: 'memo-tracking',
        title: 'Diamond Memo Return Tracking',
        description: 'Complete memo management system tracking borrowed diamonds, due dates, extensions, and automated return reminders.',
        laymenDescription: 'Track diamonds dealers borrow to show clients. Know what\'s out, when it\'s due back, send automatic reminders.',
        realWorldComparison: 'Similar to: Library book tracking, equipment rental systems, tool lending programs',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'operations', 'automation'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Never lose track of inventory. Automated reminders. Professional memo process.',
        icon: _jsx(FaHistory, {}),
        color: '#8b5cf6'
    },
    {
        id: 'bulk-purchase-discounts',
        title: 'Bulk Purchase Discount Engine',
        description: 'Automatic volume-based pricing tiers with configurable discount levels based on order quantity or total value.',
        laymenDescription: 'Like Costco pricing - buy more, pay less per unit. Automatic discounts for larger orders encourage bigger purchases.',
        realWorldComparison: 'Similar to: Costco bulk pricing, Sam\'s Club wholesale discounts, Office Depot volume pricing',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'high-roi', 'automation'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Incentivize larger orders. Clear pricing structure. Increased average order value.',
        icon: _jsx(FaMoneyBillWave, {}),
        color: '#10b981'
    },
    {
        id: 'dealer-loyalty-tiers',
        title: 'Dealer Tier & Loyalty Program',
        description: 'Multi-tier loyalty system (Bronze, Silver, Gold, Platinum) with progressive benefits based on purchase volume.',
        laymenDescription: 'Like airline status tiers but for dealers. Higher tiers get better prices, priority access, exclusive perks.',
        realWorldComparison: 'Similar to: Delta SkyMiles status, Marriott Bonvoy tiers, Amazon Business Prime',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'high-roi', 'marketing'],
        complexity: 'High',
        timeline: '4 weeks',
        value: 'Reward top dealers. Encourage repeat business. Create status competition.',
        icon: _jsx(FaMedal, {}),
        color: '#f59e0b'
    },
    {
        id: 'quick-reorder',
        title: 'Quick Reorder from Purchase History',
        description: 'One-click reorder of previous purchases with saved preferences and automatic similar item suggestions.',
        laymenDescription: 'Reorder diamonds you\'ve bought before with one click. Like Amazon\'s "Buy Again" feature for dealers.',
        realWorldComparison: 'Similar to: Amazon "Buy Again", Staples Easy Reorder, restaurant favorite orders',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'quick-win', 'customer-experience'],
        complexity: 'Low',
        timeline: '2 weeks',
        value: 'Frictionless repeat purchases. Save dealer time. Increase repeat order rate.',
        icon: _jsx(FaRedo, {}),
        color: '#06b6d4'
    },
    {
        id: 'price-negotiation-system',
        title: 'B2B Price Negotiation System',
        description: 'Structured negotiation workflow with offer/counter-offer tracking, approval chains, and automated acceptance rules.',
        laymenDescription: 'Let dealers make offers on diamonds. Set rules for auto-accepting or requiring your approval. Like eBay "Make an Offer".',
        realWorldComparison: 'Similar to: eBay Best Offer, real estate offer system, car dealership negotiations',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'high-roi'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Close more deals. Competitive advantage. Flexibility for dealers.',
        icon: _jsx(FaGavel, {}),
        color: '#ef4444'
    },
    {
        id: 'consignment-tracking',
        title: 'Consignment Inventory Tracking',
        description: 'Track consigned diamonds at dealer locations with automated settlement calculations and sales reporting.',
        laymenDescription: 'Track diamonds you leave with dealers to sell. Know what sold, what\'s still there, automatic payment calculations.',
        realWorldComparison: 'Similar to: Retail consignment stores, art gallery consignment, used bookstore consignment',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'operations'],
        complexity: 'Very High',
        timeline: '6 weeks',
        value: 'Expand distribution. Track consigned inventory. Automated settlements.',
        icon: _jsx(FaExchangeAlt, {}),
        color: '#8b5cf6'
    },
    {
        id: 'dealer-trade-in',
        title: 'Dealer Trade-In Program',
        description: 'Allow dealers to trade in diamonds toward new purchases with automated valuation and instant credit.',
        laymenDescription: 'Dealers trade old inventory for new. Like car trade-ins at dealerships. Automatic value estimates and instant credit.',
        realWorldComparison: 'Similar to: Car trade-ins, phone upgrade programs, GameStop trade-ins',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'innovation'],
        complexity: 'Very High',
        timeline: '8 weeks',
        value: 'Move old inventory. Create upgrade path. Dealer loyalty.',
        icon: _jsx(FaExchangeAlt, {}),
        color: '#ec4899'
    },
    {
        id: 'dealer-exclusive-promos',
        title: 'Dealer-Only Promotions',
        description: 'Private promotional campaigns visible only to verified dealer accounts with exclusive pricing and early access.',
        laymenDescription: 'Secret sales just for dealers. Like Costco members-only deals or trade show specials.',
        realWorldComparison: 'Similar to: Costco member specials, trade show promotions, wholesale-only sales',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'marketing', 'quick-win'],
        complexity: 'Medium',
        timeline: '2 weeks',
        value: 'Reward dealer partners. Drive bulk sales. Exclusive value perception.',
        icon: _jsx(FaPercentage, {}),
        color: '#f97316'
    },
    {
        id: 'net-payment-terms',
        title: 'NET Payment Terms (30/60/90)',
        description: 'Flexible payment terms for approved dealers with NET 30, NET 60, NET 90 options and automated invoicing.',
        laymenDescription: 'Let trusted dealers pay 30, 60, or 90 days later. Standard B2B payment terms like vendor accounts.',
        realWorldComparison: 'Similar to: Vendor payment terms, supplier invoices, construction contractor billing',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'high-roi', 'operations'],
        complexity: 'High',
        timeline: '4 weeks',
        value: 'Industry-standard terms. Cash flow for dealers. Competitive advantage.',
        icon: _jsx(FaMoneyBillWave, {}),
        color: '#10b981'
    },
    {
        id: 'credit-application',
        title: 'Online Credit Application Workflow',
        description: 'Digital credit application with document upload, credit check integration, and approval workflow management.',
        laymenDescription: 'Dealers apply for credit online like applying for a business loan. Upload docs, run checks, approve/deny digitally.',
        realWorldComparison: 'Similar to: Business credit card applications, loan applications, vendor credit requests',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'operations'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Professional onboarding. Scalable credit approval. Compliance documentation.',
        icon: _jsx(FaUserShield, {}),
        color: '#3b82f6'
    },
    {
        id: 'dealer-performance-analytics',
        title: 'Dealer Performance Analytics',
        description: 'Detailed analytics for each dealer showing purchase history, payment patterns, favorite products, and lifetime value.',
        laymenDescription: 'Know which dealers buy most, pay on time, prefer what diamonds. Like a report card for your dealer relationships.',
        realWorldComparison: 'Similar to: Salesforce account insights, distributor scorecards, vendor performance reports',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'analytics', 'high-roi'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Identify top dealers. Risk management. Personalized relationship building.',
        icon: _jsx(FaTrophy, {}),
        color: '#f59e0b'
    },
    {
        id: 'early-access-inventory',
        title: 'Early Access to New Inventory',
        description: 'Top-tier dealers get 24-48 hour exclusive access to new inventory before public release.',
        laymenDescription: 'VIP dealers see new diamonds first, like early access shopping for Black Friday. Reward your best partners.',
        realWorldComparison: 'Similar to: Sephora VIP early access, credit card presale events, product launch exclusivity',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'premium', 'marketing'],
        complexity: 'Low',
        timeline: '2 weeks',
        value: 'VIP treatment. Competitive advantage for top dealers. Status incentive.',
        icon: _jsx(FaEye, {}),
        color: '#ec4899'
    },
    {
        id: 'dealer-referral-program',
        title: 'Dealer Referral Program',
        description: 'Incentivize dealers to refer other dealers with commission tracking, automated payouts, and tiered rewards.',
        laymenDescription: 'Pay dealers who bring in other dealers. Like Dropbox referral bonuses but for B2B. Grows your dealer network.',
        realWorldComparison: 'Similar to: Referral commissions, affiliate programs, MLM networks (but professional)',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'high-roi', 'marketing'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Network effect growth. Low-cost dealer acquisition. Partner engagement.',
        icon: _jsx(FaNetworkWired, {}),
        color: '#14b8a6'
    },
    {
        id: 'volume-pricing-tiers',
        title: 'Volume Pricing Tier Calculator',
        description: 'Real-time calculator showing price breaks at different quantity levels to encourage larger orders.',
        laymenDescription: 'Show dealers "buy 10 get 5% off, buy 20 get 10% off" dynamically. Encourages upselling themselves.',
        realWorldComparison: 'Similar to: Office supply bulk pricing tables, printer ink quantity discounts',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'quick-win', 'high-roi'],
        complexity: 'Low',
        timeline: '2 weeks',
        value: 'Transparent pricing. Encourages larger orders. Self-service upselling.',
        icon: _jsx(FaChartPie, {}),
        color: '#6366f1'
    },
    {
        id: 'white-label-catalogs',
        title: 'White-Label Catalogs for Dealers',
        description: 'Generate branded PDF catalogs with dealer\'s logo and markup for their retail customers.',
        laymenDescription: 'Create catalogs with dealer\'s branding. They look like the hero, you\'re behind the scenes. Like dropshipping but professional.',
        realWorldComparison: 'Similar to: White-label products, private label goods, OEM partnerships',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'premium'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Enable dealer success. Value-added service. Sticky partnership.',
        icon: _jsx(FaPalette, {}),
        color: '#a855f7'
    },
    {
        id: 'private-dealer-marketplace',
        title: 'Private Dealer Marketplace',
        description: 'Exclusive portal where verified dealers can trade with each other directly within your ecosystem.',
        laymenDescription: 'Dealers buy and sell to each other in private marketplace. You take a small cut. Like eBay but dealer-only.',
        realWorldComparison: 'Similar to: Trade-only marketplaces, B2B exchanges, wholesale trading platforms',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'innovation', 'premium'],
        complexity: 'Very High',
        timeline: '10 weeks',
        value: 'New revenue stream. Dealer community building. Platform stickiness.',
        icon: _jsx(FaBuilding, {}),
        color: '#0ea5e9'
    },
    {
        id: 'b2b-auction-platform',
        title: 'B2B Auction Platform',
        description: 'Dealer-only auctions for wholesale lots, estate collections, and overstock with bidding and automatic settlement.',
        laymenDescription: 'Wholesale auctions for dealers only. Move bulk inventory fast. Like liquidation auctions but online.',
        realWorldComparison: 'Similar to: B-Stock wholesale auctions, Liquidation.com, estate sales',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'innovation', 'high-roi'],
        complexity: 'Very High',
        timeline: '10 weeks',
        value: 'Clear slow inventory. Market-driven pricing. Excitement factor.',
        icon: _jsx(FaGavel, {}),
        color: '#ef4444'
    },
    {
        id: 'joint-venture-opportunities',
        title: 'Joint Venture Opportunity Portal',
        description: 'Platform for proposing and managing joint purchases, co-marketing campaigns, and strategic partnerships with dealers.',
        laymenDescription: 'Team up with dealers on big opportunities. Co-invest in rare pieces, share marketing costs. Like business partnerships.',
        realWorldComparison: 'Similar to: Real estate investment partnerships, co-marketing agreements, strategic alliances',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'premium', 'enterprise'],
        complexity: 'Very High',
        timeline: '8 weeks',
        value: 'Strategic relationships. Larger deals. Shared risk and reward.',
        icon: _jsx(FaBriefcase, {}),
        color: '#8b7355'
    },
    {
        id: 'dealer-dashboard',
        title: 'Dedicated Dealer Dashboard',
        description: 'Specialized portal for dealers with account overview, credit status, order history, memo tracking, and exclusive inventory access.',
        laymenDescription: 'Dealers get their own portal like Amazon Business. See credit available, track orders, manage memos, all in one place.',
        realWorldComparison: 'Similar to: Amazon Business dashboard, Costco Business Center portal, wholesale club accounts',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'high-roi', 'customer-experience'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Self-service convenience. Professional B2B experience. Reduces support calls.',
        icon: _jsx(FaChartLine, {}),
        color: '#10b981'
    },
    {
        id: 'memo-extension-auto-approval',
        title: 'Memo Extension Auto-Approval System',
        description: 'Automated memo extension requests with configurable rules for auto-approval based on dealer tier, payment history, and extension frequency.',
        laymenDescription: 'Dealers request more time with borrowed diamonds. System auto-approves trusted dealers, flags risky ones. Like automatic hotel late checkout.',
        realWorldComparison: 'Similar to: Credit card auto-increase approvals, rental car extension requests, library book renewals',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'automation', 'high-roi'],
        complexity: 'High',
        timeline: '4 weeks',
        value: 'Reduce manual work. Instant dealer satisfaction. Risk-based automation.',
        icon: _jsx(FaClock, {}),
        color: '#8b5cf6'
    },
    {
        id: 'shared-inventory-pools',
        title: 'Shared Inventory Pool Network',
        description: 'Allow multiple dealers to pool inventory access, share fulfillment capabilities, and cross-reference stock in real-time.',
        laymenDescription: 'Dealers share inventory like co-op buying. One dealer shows a stone from another\'s stock. Like distributor networks or buying groups.',
        realWorldComparison: 'Similar to: Ace Hardware co-op, auto parts distributor networks, wholesale buying groups',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'innovation', 'premium'],
        complexity: 'Very High',
        timeline: '8 weeks',
        value: 'Expand virtual inventory. Dealer collaboration. Competitive advantage.',
        icon: _jsx(FaWarehouse, {}),
        color: '#0ea5e9'
    },
    {
        id: 'dealer-dropship-fulfillment',
        title: 'Dealer Drop-Ship Fulfillment',
        description: 'Direct shipping to dealer\'s customers with white-label packaging, branded inserts, and seamless order handoff.',
        laymenDescription: 'Ship directly to dealer\'s customer with their branding. They sell, you ship. Like Amazon FBA but for wholesale diamonds.',
        realWorldComparison: 'Similar to: Amazon FBA, Shopify dropshipping, wholesale distributor fulfillment',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'operations', 'high-roi'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Value-added service. Dealer scalability. Stickier relationships.',
        icon: _jsx(FaTruck, {}),
        color: '#10b981'
    },
    {
        id: 'dealer-coop-marketing',
        title: 'Dealer Co-Op Marketing Fund',
        description: 'Percentage-based marketing fund for dealers with campaign approval workflow, budget tracking, and reimbursement automation.',
        laymenDescription: 'Give dealers advertising budget based on purchases. They promote, you reimburse. Like manufacturer co-op advertising programs.',
        realWorldComparison: 'Similar to: Car dealer co-op ads, Intel Inside marketing funds, franchise advertising support',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'marketing', 'high-roi'],
        complexity: 'Medium',
        timeline: '4 weeks',
        value: 'Amplify marketing reach. Dealer empowerment. Brand consistency.',
        icon: _jsx(FaDollarSign, {}),
        color: '#f59e0b'
    },
    {
        id: 'exclusive-territory-rights',
        title: 'Exclusive Territory Rights Management',
        description: 'Assign geographic territories to top dealers with exclusivity protection, territory mapping, and conflict resolution workflows.',
        laymenDescription: 'Give top dealers exclusive rights to sell in their region. Like franchise territories or distributor exclusivity agreements.',
        realWorldComparison: 'Similar to: Franchise territories, distributor exclusivity zones, beverage distributor rights',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'premium', 'enterprise'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Dealer loyalty. Protected investment. Premium positioning.',
        icon: _jsx(FaMapMarkedAlt, {}),
        color: '#ef4444'
    },
    {
        id: 'price-match-guarantee',
        title: 'Dealer Price Match Guarantee',
        description: 'Automated competitor price monitoring with guaranteed best price for dealers, including retroactive credit for recent purchases.',
        laymenDescription: 'Match competitors\' wholesale prices automatically. Even give credit if price drops after purchase. Like Costco price adjustments.',
        realWorldComparison: 'Similar to: Costco price adjustment policy, Best Buy price match, credit card price protection',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'high-roi', 'automation'],
        complexity: 'High',
        timeline: '6 weeks',
        value: 'Dealer confidence. Competitive edge. Loyalty building.',
        icon: _jsx(FaShieldAlt, {}),
        color: '#14b8a6'
    },
    {
        id: 'rush-production-custom',
        title: 'Rush Production for Custom Orders',
        description: 'Priority queue for custom cutting and setting with expedited timelines, progress tracking, and premium rush fees.',
        laymenDescription: 'Fast-track custom work for dealers with tight deadlines. Pay extra, get it faster. Like FedEx overnight for manufacturing.',
        realWorldComparison: 'Similar to: FedEx overnight shipping, rush printing services, expedited manufacturing',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'operations', 'premium'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Premium revenue stream. Dealer satisfaction. Competitive advantage.',
        icon: _jsx(FaRocket, {}),
        color: '#ec4899'
    },
    {
        id: 'dealer-showroom-scheduling',
        title: 'Dealer Showroom Visit Scheduler',
        description: 'Book appointments to visit your showroom, reserve specific diamonds for viewing, and schedule expert consultations.',
        laymenDescription: 'Dealers book showroom visits online with calendar. Reserve specific stones to see. Like scheduling a test drive or property showing.',
        realWorldComparison: 'Similar to: Car dealership test drives, real estate showings, B2B showroom appointments',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'customer-experience', 'quick-win'],
        complexity: 'Low',
        timeline: '2 weeks',
        value: 'Professional experience. Efficient showroom management. Better preparation.',
        icon: _jsx(FaCalendarAlt, {}),
        color: '#6366f1'
    },
    {
        id: 'live-wholesale-bidding',
        title: 'Live Wholesale Bidding Platform',
        description: 'Dutch auction-style live bidding events for wholesale lots with countdown timers, real-time price drops, and instant purchase.',
        laymenDescription: 'Price starts high, drops every minute until someone buys. Exciting wholesale sales like Dutch flower auctions or stock liquidations.',
        realWorldComparison: 'Similar to: Dutch flower auctions, Woot deals, flash sale countdown events',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'innovation', 'high-roi'],
        complexity: 'Very High',
        timeline: '7 weeks',
        value: 'Move inventory fast. Create urgency. Engaging experience.',
        icon: _jsx(FaGavel, {}),
        color: '#f97316'
    },
    {
        id: 'dealer-diamond-swaps',
        title: 'Dealer-to-Dealer Diamond Exchange',
        description: 'Peer-to-peer marketplace for dealers to swap inventory with escrow protection, quality verification, and automated matching.',
        laymenDescription: 'Dealers trade diamonds with each other safely. Like trading baseball cards but with escrow and verification. Platform takes small fee.',
        realWorldComparison: 'Similar to: Gun broker exchanges, collectible trading platforms, peer-to-peer marketplaces',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'innovation', 'premium'],
        complexity: 'Very High',
        timeline: '9 weeks',
        value: 'Dealer community building. Platform stickiness. Transaction revenue.',
        icon: _jsx(FaExchangeAlt, {}),
        color: '#a855f7'
    },
    {
        id: 'seasonal-buying-guides',
        title: 'Seasonal Buying Guides for Dealers',
        description: 'AI-powered seasonal demand forecasts with recommended inventory mix, trending styles, and pre-order suggestions for holidays.',
        laymenDescription: 'Tell dealers what to stock for Valentine\'s, holidays, wedding season. Like fashion trend forecasts or retail buying guides.',
        realWorldComparison: 'Similar to: Retail buyer trend reports, fashion season forecasts, Pantone color of the year',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'ai-powered', 'marketing'],
        complexity: 'High',
        timeline: '5 weeks',
        value: 'Thought leadership. Dealer success. Increased seasonal sales.',
        icon: _jsx(FaChartLine, {}),
        color: '#3b82f6'
    },
    {
        id: 'inventory-consignment-revenue',
        title: 'Inventory Consignment with Revenue Share',
        description: 'Place inventory at dealer locations on consignment with automated revenue sharing, sales tracking, and settlement calculations.',
        laymenDescription: 'Leave diamonds at dealer stores, split revenue when sold. Platform tracks everything automatically. Like consignment retail but wholesale.',
        realWorldComparison: 'Similar to: Gallery art consignment, vintage clothing consignment, used equipment consignment',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'operations', 'premium'],
        complexity: 'Very High',
        timeline: '8 weeks',
        value: 'Expand distribution. Dealer stocking. Automated settlements.',
        icon: _jsx(FaReceipt, {}),
        color: '#10b981'
    },
    {
        id: 'split-shipment-locations',
        title: 'Split Shipment to Multiple Locations',
        description: 'Single order shipped to multiple dealer locations with consolidated invoicing, individual tracking, and coordinated delivery.',
        laymenDescription: 'Dealers with multiple stores order once, ship to each location. One invoice, many destinations. Like B2B multi-location delivery.',
        realWorldComparison: 'Similar to: Multi-location restaurant deliveries, franchise shipments, retail chain distribution',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'operations', 'quick-win'],
        complexity: 'Medium',
        timeline: '3 weeks',
        value: 'Dealer convenience. Larger orders. Operational efficiency.',
        icon: _jsx(FaLayerGroup, {}),
        color: '#06b6d4'
    },
    {
        id: 'dealer-collaboration-custom',
        title: 'Dealer Collaboration on Large Orders',
        description: 'Platform for multiple dealers to co-invest in expensive custom pieces, share costs and profits with built-in partnership agreements.',
        laymenDescription: 'Dealers team up to buy expensive custom diamonds together. Share cost, share profit. Like real estate investment partnerships.',
        realWorldComparison: 'Similar to: Real estate syndication, group buying, investment partnerships',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'innovation', 'premium'],
        complexity: 'Very High',
        timeline: '10 weeks',
        value: 'Enable bigger deals. Risk sharing. Community building.',
        icon: _jsx(FaProjectDiagram, {}),
        color: '#8b7355'
    },
    {
        id: 'virtual-trade-show-booth',
        title: 'Virtual Trade Show Booth Platform',
        description: 'Year-round virtual trade show presence with 3D booth, live video demos, appointment scheduling, and exclusive show pricing.',
        laymenDescription: 'Permanent virtual trade show booth online. Live demos, special show pricing, book appointments. Like JCK show but always open.',
        realWorldComparison: 'Similar to: Virtual conference booths, online trade shows, B2B virtual events',
        category: 'B2B',
        tags: ['b2b', 'dealer-tools', 'wholesale', 'innovation', 'marketing'],
        complexity: 'High',
        timeline: '6 weeks',
        value: 'Always accessible. Global reach. Trade show economics without travel.',
        icon: _jsx(FaVideo, {}),
        color: '#ec4899'
    }
];
export const CATEGORIES = [
    'All Categories',
    'Core',
    'Innovation',
    'Customer Experience',
    'Operations',
    'Analytics',
    'Integration',
    'Security',
    'Marketing',
    'Mobile',
    'International',
    'Premium',
    'Technical',
    'Education',
    'Automation',
    'B2B'
];
export const COMPLEXITIES = [
    'All Levels',
    'Low (Quick Win)',
    'Medium',
    'High',
    'Very High'
];
export const TAGS = [
    'high-roi',
    'quick-win',
    'technical',
    'non-technical',
    'mobile',
    'ai-powered',
    'automation',
    'real-time',
    'premium',
    'enterprise',
    'b2b',
    'b2c',
    'customer-experience',
    'operations',
    'marketing',
    'security',
    'dealer-tools',
    'wholesale',
    'education'
];
